import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import UsersPopup from "../popups/UsersPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";
import { apiGetAllSellers } from "../services/SellersService";
import { apiDeleteUser, apiGetAllUsers, apiGetAllUsersForSeller } from "../services/UsersService";
import { SelectBox } from "../components/common/SelectBox";
import './UzivatelePage.css';

interface Props {
  contextLevel: string;
}

const entityName = 'user';

const UzivatelePage = (props: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Users' });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [] });
  const [sellers, setSellers] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const userInfo = useSelector((state: any) => state.userInfo);

  const initFormData = {
    sellerId: userInfo.SellerId || null,

    userPopupData: {
      visible: false,
      id: undefined
    }
  }

  const [formData, setFormData] = useState(initFormData);

  const setRoles = () => {
    setCanCreate(checkCrud(entityName, 'create', userInfo));
    setCanRead(checkCrud(entityName, 'read', userInfo));
    setCanUpdate(checkCrud(entityName, 'update', userInfo));
    setCanDelete(checkCrud(entityName, 'delete', userInfo));
    setIsAdmin(checkCrud(entityName, 'admin', userInfo));
  }

  useEffect(() => {
    setRoles();
  }, [userInfo.UserRoles])

  const dispatch = useDispatch();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [formData]);

  const loadData = () => {
    apiGetAllUsersForSeller(formData.sellerId).then((responseData) => {
      setData({
        rows: responseData.data,
      });

      setLoading(false);
    });

    apiGetAllSellers().then((resp: any) => {
      let arr = resp.data.map((element) => {
        return {
          value: element.id,
          label: element.name
        };
      });
      setSellers(arr);
    });
  };

  const handleDeleteRow = (row) => {
    apiDeleteUser(row.id)
      .then((responseData) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteSuccess'), row.firstName, row.lastName), type: 'success' }));
        loadData();

        setLoading(false);
      })
      .catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteFailure'), row.firstName, row.lastName), type: 'error' }));
      });
  }

  const translationModel = { prefix: 'Users' };

  return (
    <TranslationProvider value={translationModel}>
      {formData.userPopupData.visible && (
        <UsersPopup
          userInfo={userInfo}
          id={formData.userPopupData.id}
          sellerId={formData.sellerId}
          isUserAdmin={isAdmin}
          refreshData={() => loadData()}
          sellers={sellers}
          handleCancel={() => {
            setFormData({ ...formData, userPopupData: { visible: false, id: undefined } });
          }}
          isAdmin={isAdmin}
        />
      )}
      <TopBarWithBreadCrumb
        contextLevel={props.contextLevel}
        pageLevel={t('Label')}
        rightAreaComponent={
          <AddButtonInTopBar
            setPopupData={setFormData}
            text={t('CreateText')}
            handleAction={() => {
              setFormData({ ...formData, userPopupData: { visible: true, id: undefined } });
            }}
            enabled={canCreate}
          />
        }
      />

      <PageContent>
        <>
          {isAdmin && (
            <SelectBox
              placeholder={t('SelectSeller')}
              dataField={'sellerId'}
              isDisabled={false}
              formData={formData}
              value={formData.sellerId}
              options={sellers}
              label={t('SelectSeller')}
              customLeftCols={'col-sm-1'}
              customRightCols={'col-sm-3'}
              className="zIndex10"
              setStateMethod={setFormData}
            />
          )}

          <DataGrid
            loading={loading}
            rows={data.rows}
            showDeleteButton={canDelete}
            showEditButton={canUpdate}
            handleEditAction={(id: any) => {
              setFormData({ ...formData, userPopupData: { visible: true, id: id } });
            }}
            onDeleteRow={handleDeleteRow}
            cols={[
              { name: t('firstname'), dataField: "firstname", isLinkToItem: true, sortable: true, filterable: true, filterType: "text" },
              { name: t('lastname'), dataField: "lastname", sortable: true, filterable: true, filterType: "text" },
              { name: t('login'), dataField: "login", sortable: true, filterable: true, filterType: "text" },
              { name: t('email'), dataField: "email", sortable: true, filterable: true, filterType: "text" },
              { name: t('Note'), dataField: "noteInternal", sortable: true, filterable: true, filterType: "text" }
            ]}
          />
        </>
      </PageContent>
    </TranslationProvider>
  );
};

export default UzivatelePage;
